import React from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { STATUS, STATUS_STYLES, catLabel } from "@/mock/data";
import { InsightList } from "@/components/InsightList";
import { runMatterAction } from "@/lib/matterActions";
import { useMarcarHecho } from "@/components/asuntos/CierreAsunto";
import { PersonaChip, usePersona } from "@/components/personas/Avatar";
import { eur } from "@/lib/gastos";

const PersonBadge = ({ id, person }) => {
  const fm = usePersona(id);
  if (!fm && !person) return null;
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#4A5550] bg-[#F2EDE4] rounded-full pl-1 pr-2.5 py-1 max-w-full">
      {fm ? <PersonaChip persona={fm} /> : <><Icons.User size={13} /> {person}</>}
    </span>
  );
};

const PetBadge = ({ name }) =>
  name ? (
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E5645] bg-[#E8F0EC] rounded-full px-2.5 py-1">
      <Icons.PawPrint size={13} /> {name}
    </span>
  ) : null;

// Tarjeta grande para "Requiere tu atención" y "Próximamente".
export const MatterCard = ({ matter, index = 0 }) => {
  const navigate = useNavigate();
  const marcarHecho = useMarcarHecho();
  const style = STATUS_STYLES[matter.status];
  const Icon = Icons[matter.icon] || Icons.FileText;

  const open = () => navigate(`/asunto/${matter.id}`);

  // En tarjeta mostramos la recomendación y lo que conviene verificar (no todos los datos).
  const cardInsights = matter.insights.filter(
    (i) => i.type === "recommendation" || i.type === "verify"
  );

  return (
    <div
      data-testid={`matter-card-${matter.id}`}
      onClick={open}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && open()}
      className={`aldia-fade-up cursor-pointer bg-white border border-[#E5DFD5] ${style.leftBorder} border-l-[6px] rounded-2xl p-5 sm:p-6 shadow-sm hover:shadow-md transition-shadow duration-200`}
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div className="flex items-start gap-4">
        <div
          className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${style.indicator}1A` }}
        >
          <Icon size={26} style={{ color: style.indicator }} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 ${style.badgeBg} ${style.badgeText} text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
              {style.label}
            </span>
            <PersonBadge id={matter.family_member_id} person={matter.person} />
            <PetBadge name={matter.petName} />
          </div>
          <h3 className="font-heading text-lg sm:text-xl font-bold text-[#1C2421] mt-2 leading-snug">
            {matter.title}
          </h3>
          <p className="text-sm font-medium text-[#6E7A74] mt-0.5">
            {catLabel(matter.category)}
            {matter.provider ? ` · ${matter.provider}` : ""}
          </p>
        </div>
        <Icons.ChevronRight size={22} className="text-[#C4BDB0] shrink-0 mt-1" />
      </div>

      <p className="text-base sm:text-lg text-[#1C2421] mt-4 leading-relaxed flex items-center gap-2">
        <Icons.CalendarClock size={18} className="text-[#3A6B56] shrink-0" />
        <span className="font-semibold">{matter.due_label}</span>
      </p>

      {matter.amount != null && (
        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-base">
          <span className="text-[#1C2421]">
            Importe: <strong className="font-bold">{eur(matter.amount)}</strong>
          </span>
          {matter.previous_amount != null && matter.previous_amount !== matter.amount && (
            <span className="text-[#6E7A74]">Anterior: {eur(matter.previous_amount)}</span>
          )}
          {matter.priceDiff && (
            <span className="inline-flex items-center gap-1 text-[#B91C1C] font-bold">
              <Icons.TrendingUp size={16} /> {matter.priceDiff}
            </span>
          )}
        </div>
      )}

      {cardInsights.length > 0 && (
        <div className="mt-4">
          <InsightList insights={cardInsights} />
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2.5">
        {matter.actions.map((action, i) => {
          const primary = i === 0 && matter.status !== STATUS.CONTROLLED;
          return (
            <button
              key={action}
              data-testid={`matter-action-${matter.id}-${toSlug(action)}`}
              onClick={(e) => {
                e.stopPropagation();
                if (action === "Hecho") marcarHecho(matter);
                else if (action === "Nueva fecha") navigate(`/asunto/${matter.id}?nueva=1`);
                else runMatterAction(action, matter, navigate);
              }}
              className={
                primary
                  ? "min-h-[48px] px-5 rounded-xl font-semibold text-base bg-[#3A6B56] text-white hover:bg-[#2E5645] transition-colors flex items-center gap-2"
                  : "min-h-[48px] px-5 rounded-xl font-semibold text-base bg-[#F2EDE4] text-[#1C2421] border border-[#E5DFD5] hover:bg-[#E5DFD5] transition-colors flex items-center gap-2"
              }
            >
              {action}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Fila compacta para "Bajo control": reduce scroll, sigue siendo pulsable.
export const CompactMatterRow = ({ matter, index = 0 }) => {
  const navigate = useNavigate();
  const style = STATUS_STYLES[matter.status];
  const Icon = Icons[matter.icon] || Icons.FileText;

  return (
    <button
      data-testid={`matter-compact-${matter.id}`}
      onClick={() => navigate(`/asunto/${matter.id}`)}
      className="aldia-fade-up w-full flex items-center gap-3 bg-white border border-[#E5DFD5] rounded-xl px-4 py-3 text-left hover:border-[#16A34A] hover:shadow-sm transition-all"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: style.indicator }} />
      <Icon size={20} className="text-[#6E7A74] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-heading font-bold text-base text-[#1C2421] truncate">
          {matter.title}
        </p>
        <p className="text-sm text-[#6E7A74] flex items-center gap-1.5 min-w-0">
          {matter.petName ? <span className="truncate">{matter.petName} · {matter.due_label}</span>
            : <><PersonaChip id={matter.family_member_id} className="shrink" /><span className="truncate shrink-0">· {matter.due_label}</span></>}
        </p>
      </div>
      <Icons.ChevronRight size={20} className="text-[#C4BDB0] shrink-0" />
    </button>
  );
};

function toSlug(s) {
  return s
    .toLowerCase()
    .replace(/[áàä]/g, "a").replace(/[éèë]/g, "e").replace(/[íìï]/g, "i")
    .replace(/[óòö]/g, "o").replace(/[úùü]/g, "u").replace(/ñ/g, "n")
    .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
